/**
 * Work-order wire types. Keep in lockstep with proxy-server
 * `internal/ontology/workorders/domain.go` (+ handler request envelopes).
 */

export type WorkOrderPriority = 'critical' | 'high' | 'medium' | 'low'

export type WorkOrderStatus = 'open' | 'in_progress' | 'completed'

/** List-filter status: `open` means non-completed (open + in_progress). */
export type WorkOrderListStatus = 'open' | 'completed' | 'all'

export type WorkOrderSystem = 'internal' | 'servicenow' | 'sap' | 'byodb'

export interface WorkOrderComment {
  author: string
  body: string
  created_at: string
}

export interface WorkOrderProvenance {
  source_agent?: string
  source_action_item_table?: string
  source_action_item_id?: string
  source_insight_id?: string
  source_run_id?: string
}

export interface WorkOrder {
  /** Composite id: `{system}/{integration_id?}/{native_id}`. */
  id: string
  /** AIP-122 resource name, e.g. `work-orders/internal/{uuid}`. */
  name?: string
  work_order_system: WorkOrderSystem | string
  source_ref: string
  source_label?: string
  source_url?: string
  instruction: string
  priority: WorkOrderPriority
  location?: string
  equipment: string
  status: WorkOrderStatus
  created_at: string
  completed_at?: string | null
  completed_by?: string
  /** Populated on Get only (never on List). */
  comments?: WorkOrderComment[]
  /** Count of comments — set on List and Get for the row badge. */
  comment_count?: number
  /** Internal agent-created rows only. */
  provenance?: WorkOrderProvenance | null
}

export interface WorkOrderSourceError {
  source: string
  message: string
}

export interface WorkOrderListResult {
  work_orders: WorkOrder[]
  next_page_token?: string
  source_errors?: WorkOrderSourceError[]
}

export interface WorkOrderListFilters {
  status?: WorkOrderListStatus
  /** Comma-separated priorities, or an array (joined in the query). */
  priority?: WorkOrderPriority[] | string
  q?: string
  source?: string
  page_size?: number
  page_token?: string
}

export interface CompleteWorkOrderPayload {
  completed_by: string
}

export interface CommentWorkOrderPayload {
  author: string
  body: string
}

/** Sources that support complete + comment via the federated proxy API. */
export function workOrderSupportsWrites(wo: Pick<WorkOrder, 'work_order_system'>): boolean {
  return wo.work_order_system === 'internal' || wo.work_order_system === 'servicenow'
}
